// material ui
import { Grid, Typography } from "@mui/material";
import { FirestoreInventoryItemProps } from "types/auth";
import RawWoodInputInformationCertificationField, {
  InventoryCertificationTag,
} from "./rawWoodInputInformationCertificationField";

// project import
import { useTranslation } from "utils/locales/utilityFunctions";

interface RawWoodInputInformationCertificationProps {
  disableInputs: boolean;
  certificationNumbers: NonNullable<FirestoreInventoryItemProps["certificationNumbers"]>;
  certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>;
  legacyCertificationNumber: string;
  certificationFields: InventoryCertificationFieldConfig[];
  pefc: boolean;
  fsc: boolean;
  ukwas: boolean;
  rbp: boolean;
  sbp: boolean;
  setCertification: (certification: string) => void;
  setCertificationNumbers: (
    certificationNumbers: NonNullable<FirestoreInventoryItemProps["certificationNumbers"]>,
  ) => void;
  setCertificationClaims: (
    certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>,
  ) => void;
}

export type InventoryCertificationFieldConfig = {
  tag: InventoryCertificationTag;
  claimOptions?: readonly string[];
  showCertificateNumberInput: boolean;
};

const RawWoodInputInformationCertification = ({
  disableInputs,
  certificationNumbers,
  certificationClaims,
  legacyCertificationNumber,
  certificationFields,
  fsc,
  pefc,
  sbp,
  rbp,
  ukwas,
  setCertification,
  setCertificationNumbers,
  setCertificationClaims,
}: RawWoodInputInformationCertificationProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const certificateNumberPlaceholder = translate("input-dialog-raw-wood-tab-information-certificate");

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ mb: -2 }}>
          {translate("input-dialog-raw-wood-tab-information-certification")}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          {certificationFields.map(({ tag, claimOptions, showCertificateNumberInput }) => (
            <RawWoodInputInformationCertificationField
              key={tag}
              tag={tag}
              label={translate(`input-dialog-raw-wood-tab-information-${tag.toUpperCase()}`)}
              selected={{ fsc, pefc, rbp, sbp, ukwas }[tag]}
              value={certificationNumbers[tag] || ""}
              placeholder={certificateNumberPlaceholder}
              claim={certificationClaims[tag as keyof typeof certificationClaims]}
              claimOptions={claimOptions}
              showCertificateNumberInput={showCertificateNumberInput}
              disabled={disableInputs}
              onToggle={setCertification}
              onValueChange={(changedTag, value) =>
                setCertificationNumbers({ ...certificationNumbers, [changedTag]: value })
              }
              onClaimChange={(changedTag, claim) =>
                setCertificationClaims({ ...certificationClaims, [changedTag]: claim })
              }
            />
          ))}
        </Grid>
      </Grid>

      {legacyCertificationNumber && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            {`${translate("biomass-project-form-legacy-certificate-number")}: ${legacyCertificationNumber}`}
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default RawWoodInputInformationCertification;
